import type { AppError } from '@core/api-clients/types.js';

import { t } from '../translations.js';

export type ResolvedAppError = {
  /** User-facing Dutch message — the per-code translation, or a generic fallback. */
  main: string;
  /**
   * Technical detail to render subtly when no Dutch translation existed for
   * the code. Carries the backend's English `message` so the user (and
   * support) still see what went wrong. `undefined` when a Dutch translation
   * was found and `main` is already specific.
   */
  technical?: string;
};

/**
 * Maps an `AppError` to its rendering inputs:
 * - Known code with a string entry in `t.errors` → `{ main: <Dutch> }`
 * - Known code with a function entry → `{ main: <Dutch with details> }`
 * - Unknown code, or a function entry without the `details` it interpolates →
 *   `{ main: t.app.error.title, technical: error.message }`
 *
 * Used by `ErrorPanel` (renders the two parts as separate lines) and
 * `reportMutationError` / `formatApiError` (collapse to one line for toast / inline
 * surfaces). See `docs/features/error-handling-policy/design.md`.
 */
export function resolveAppError(error: AppError): ResolvedAppError {
  const translation = (t.errors as Record<string, unknown>)[error.code];
  if (typeof translation === 'string') return { main: translation };
  // A function-form translation interpolates `error.details`; an error that
  // violates that contract (no details) takes the generic branch rather than
  // crashing on `fn(undefined)`.
  if (typeof translation === 'function' && 'details' in error) {
    const fn = translation as (details: unknown) => string;
    return { main: fn(error.details) };
  }
  return { main: t.app.error.title, technical: error.message };
}
